"use client";

import React, { useState, useEffect } from "react";
import Autoplay from "embla-carousel-autoplay";
import {
  Carousel,
  CarouselContent,
  type CarouselApi,
} from "@/components/ui/carousel";
import { cn } from "@/utils/utils";
import {
  type CarouselItemType,
  OnboardingCarouselItem,
} from "./onboarding-carousel-item";

interface OnboardingCarouselProps {
  items: CarouselItemType[];
  className?: string;
}

export const OnboardingCarousel = ({
  items,
  className,
}: OnboardingCarouselProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [api, setApi] = useState<CarouselApi>();
  const [isReady, setIsReady] = useState(false);

  const autoplayPlugin = Autoplay({
    delay: 5000,
    stopOnInteraction: false,
    stopOnMouseEnter: false,
  });

  const handleSelect = (api: CarouselApi) => {
    if (!api) return;
    setSelectedIndex(api.selectedScrollSnap());
  };

  useEffect(() => {
    if (!api || items.length === 0) return;

    const initCarousel = () => {
      api.reInit();
      setIsReady(true);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(initCarousel);
    });
  }, [api, items.length]);

  return (
    <div className="relative flex h-full w-full max-w-full">
      <Carousel
        plugins={[autoplayPlugin]}
        opts={{
          align: "center",
          loop: true,
        }}
        className={cn(
          "h-full w-full max-w-full content-center opacity-0 transition-opacity duration-700 ease-out",
          isReady && "opacity-100",
          className,
        )}
        setApi={setApi}
        handleSelect={handleSelect}
      >
        <CarouselContent>
          {items.map((item, index) => (
            <OnboardingCarouselItem
              key={item.id}
              item={item}
              isActive={index === selectedIndex}
            />
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
};
